/** Список акций бренда — статусы, черновики, копии. */
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Skeleton, fmtDay, num } from '@allswipes/ui';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { canPublish, useConsole } from '../session';
import { useToast } from '../toast';
import { statusLabel } from './Dashboard';

export function CampaignsScreen() {
  const list = useResource(() => api.consoleCampaigns(), []);
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useConsole();

  const duplicate = async (id: string) => {
    try {
      const copy = await api.duplicate(id);
      toast('Создана копия — проверь даты');
      navigate(`/create/${copy.id}`);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось скопировать');
    }
  };

  return (
    <Layout
      title="Акции"
      subtitle="после запуска условия не меняются — для изменений делай копию"
      action={
        <Link to="/create" className="btn pri">
          Создать акцию
        </Link>
      }
    >
      {list.loading && <Skeleton count={4} height={52} />}
      {list.data?.campaigns.length === 0 && (
        <Empty icon="flame" title="Пока нет акций">
          Создай первую — мастер проведёт по пяти шагам и сразу покажет страницу акции.
          <div style={{ marginTop: 12 }}>
            <Link to="/create" className="btn pri">
              Создать акцию
            </Link>
          </div>
        </Empty>
      )}

      {list.data && list.data.campaigns.length > 0 && (
        <Card className="flush">
          <table className="tbl" style={{ padding: 15 }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 15 }}>Акция</th>
                <th>Статус</th>
                <th>Сроки</th>
                <th>Участники</th>
                <th>Билеты</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data.campaigns.map((c) => (
                <tr key={c.id} className="clickable">
                  <td style={{ paddingLeft: 15 }}>
                    <Link to={c.status === 'draft' ? `/create/${c.id}` : `/campaigns/${c.id}`}>
                      <div className="h2">{c.name}</div>
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {c.prize_title || '—'}
                      </div>
                    </Link>
                  </td>
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {fmtDay(c.start_at)}
                    <br />— {fmtDay(c.end_at)}
                  </td>
                  <td className="mono">{num(c.participants)}</td>
                  <td className="mono">{num(c.tickets)}</td>
                  <td style={{ textAlign: 'right', paddingRight: 15 }}>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      {c.status === 'draft' ? (
                        <Link to={`/create/${c.id}`} className="btn out sm">
                          Продолжить
                        </Link>
                      ) : (
                        <Link to={`/campaigns/${c.id}`} className="btn out sm">
                          Открыть
                        </Link>
                      )}
                      {canPublish(me?.member.role) && (
                        <Button size="sm" variant="ghost" onClick={() => void duplicate(c.id)}>
                          Копия
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Layout>
  );
}

export function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'active':
      return (
        <Badge tone="live" dot>
          идёт
        </Badge>
      );
    case 'scheduled':
      return <Badge>запланирована</Badge>;
    case 'ended':
      return <Badge tone="warn">приём закрыт</Badge>;
    case 'drawn':
      return <Badge tone="good">розыгрыш проведён</Badge>;
    default:
      return <Badge>{statusLabel(status)}</Badge>;
  }
}
